import api from "./api"

// Get credits for a customer
export const getCredits = async (customerId, organizationId, includeList = true) => {
  // If organizationId is provided, include it in params, otherwise rely on server using JWT token
  const params = { customerId }
  if (organizationId) {
    params.organizationId = organizationId
  }
  if (!includeList) {
    params.summaryOnly = true
  }

  const response = await api.get("/credits", { params })
  return response.data
}

// Get credit summary for a customer (total only, no list)
export const getCreditsSummary = async (customerId, organizationId) => {
  return getCredits(customerId, organizationId, false)
}

// Get credits for an entire organization
export const getCreditsByOrganization = async (organizationId) => {
  const params = { organizationId }
  const response = await api.get("/credits/organization", { params })
  return response.data
}

/**
 * Fetch paginated credits for an organization
 * @param {Object} params - { organizationId, page, limit, search }
 * @returns {Promise<Object>} { success, count, data, limit, page }
 */
export const getCreditsByOrganizationPaginated = async (params = {}) => {
  // Ensure params is always a valid object
  const validParams = params && typeof params === "object" ? params : {};

  // Get organizationId from user context if not provided
  if (!validParams.organizationId) {
    const user = JSON.parse(localStorage.getItem("user") || "{}")
    if (user && user.organizationId) {
      validParams.organizationId = user.organizationId
    }
  }

  const response = await api.get("/credits/organization/paginated", { params: validParams });
  return response.data;
};

// Update the addCredits function to use type instead of feature
export const addCredits = async (
  customerId,
  amount,
  type = "manual_addition", // Changed from feature to type
  description = "Manual credit addition",
  organizationId,
) => {
  const payload = {
    customerId,
    amount: Number(amount),
    type, // Changed from feature to type
    description,
  }

  if (organizationId) {
    payload.organizationId = organizationId
  }

  const response = await api.post("/credits/add", payload)
  return response.data
}

// Update the deductCredits function to use type instead of feature
export const deductCredits = async (
  customerId,
  amount,
  type = "manual_deduction", // Changed from feature to type
  description = "Manual credit deduction",
  organizationId,
) => {
  const payload = {
    customerId,
    amount: Number(amount),
    type, // Changed from feature to type
    description,
  }

  if (organizationId) {
    payload.organizationId = organizationId
  }

  const response = await api.post("/credits/use", payload)
  return response.data
}

// Get credit usage history for a customer
export const getCreditUsage = async (customerId, organizationId) => {
  // If organizationId is provided, include it in params, otherwise rely on server using JWT token
  const params = { customerId }
  if (organizationId) {
    params.organizationId = organizationId
  }

  const response = await api.get("/credits/usage", { params })
  return response.data
}

// Get credit usage for an entire organization
export const getCreditUsageByOrganization = async (organizationId, period = null) => {
  const params = { organizationId }
  if (period) {
    params.period = period
  }

  const response = await api.get("/credits/usage/organization", { params })
  return response.data
}

/**
 * Fetch paginated credit usage for an organization
 * @param {Object} params - { organizationId, page, limit, search }
 * @returns {Promise<Object>} { success, count, data, limit, page }
 */
export const getCreditUsageByOrganizationPaginated = async (params = {}) => {
  // Ensure params is always a valid object
  const validParams = params && typeof params === "object" ? params : {};

  // Get organizationId from user context if not provided
  if (!validParams.organizationId) {
    const user = JSON.parse(localStorage.getItem("user") || "{}")
    if (user && user.organizationId) {
      validParams.organizationId = user.organizationId
    }
  }

  const response = await api.get("/credits/usage/organization/paginated", { params: validParams });
  return response.data;
};

// Get credit usage for a specific period
export const getCreditUsageByPeriod = async (customerId, startDate = null, endDate = null, organizationId = null) => {
  const params = {
    customerId,
  }

  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  if (organizationId) params.organizationId = organizationId

  const response = await api.get("/credits/usage/period", { params })
  return response.data
}

// Get combined credit history (both additions and usage)
export const getCombinedCreditHistory = async (customerId, organizationId) => {
  const params = { customerId }
  if (organizationId) {
    params.organizationId = organizationId
  }

  const response = await api.get("/credits/history", { params })
  return response.data
}

// Get paginated combined credit history
export const getCombinedCreditHistoryPaginated = async (customerId, organizationId, page = 1, limit = 20) => {
  const params = { customerId, page, limit }
  if (organizationId) {
    params.organizationId = organizationId
  }

  const response = await api.get("/credits/history/paginated", { params })
  return response.data
}

// Rename getCreditFeatures to getCreditTypes
export const getCreditTypes = async () => {
  const response = await api.get("/credits/types") // Changed from /credits/features to /credits/types
  return response.data
}

